/**
 * Session Guard — proactive broken-session detection.
 *
 * Runs synchronously at boot time (before any React component mounts).
 * Detects the "user record in localStorage but no auth tokens" state that
 * causes every API call to return 403 silently, then redirects to /login
 * immediately instead of letting the app mount into a permanently broken state.
 *
 * Root cause this fixes (PR #7776):
 *   1. JWT access token expires → `needsRefresh()` fires → refresh token is
 *      also expired → `clearTokens()` removes all token keys.
 *   2. The `user` key remains (it is only cleared on explicit logout or by
 *      `handleUnauthorized`).
 *   3. On next page load `needsRefresh()` returns false (no access token),
 *      so no preflight refresh runs. `getAuthHeader()` returns null.
 *   4. Every request goes out without an Authorization header → Django returns
 *      403 (anonymous request, not 401) → the 401 refresh handler never fires
 *      → the app is silently stuck in a 403 loop with no recovery path until
 *      the user manually clears localStorage.
 *
 * This guard runs before `<App />` renders and short-circuits the mount,
 * so zero API calls are made in a broken-session state.
 */

import { isUuid } from '../../../shared/utils/tenant';

/** Storage keys must match shared/services/jwtConstants.ts exactly. */
const KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  LEGACY_TOKEN: 'authToken',
  USER: 'user',
} as const;

/**
 * Return true when the current localStorage state represents a valid or
 * potentially-valid session (i.e., the normal app flow can proceed).
 *
 * Returns false when the session is definitively broken:
 *   - A `user` record exists (so the UI would show "logged in"), AND
 *   - No auth token of ANY kind is present.
 */
export function isSessionHealthy(): boolean {
  try {
    const user = localStorage.getItem(KEYS.USER);

    // Not logged in — healthy (will show login page via normal routing).
    if (!user) return true;

    // Logged in: healthy if at least one token type is present.
    const hasJwtAccess = Boolean(localStorage.getItem(KEYS.ACCESS_TOKEN));
    const hasJwtRefresh = Boolean(localStorage.getItem(KEYS.REFRESH_TOKEN));
    const hasLegacy = Boolean(localStorage.getItem(KEYS.LEGACY_TOKEN));

    return hasJwtAccess || hasJwtRefresh || hasLegacy;
  } catch {
    // localStorage unavailable (private-browsing edge case) — let the app
    // proceed; the API interceptors will handle auth failures normally.
    return true;
  }
}

/**
 * Clear the broken session state and redirect to /login.
 *
 * Removes only the `user` key (tokens are already gone by definition when
 * this is called). Preserves other localStorage data (e.g., theme, layout).
 */
export function clearBrokenSession(): void {
  try {
    localStorage.removeItem(KEYS.USER);
    // Also nuke any token keys that might be partially present.
    localStorage.removeItem(KEYS.ACCESS_TOKEN);
    localStorage.removeItem(KEYS.REFRESH_TOKEN);
    localStorage.removeItem(KEYS.LEGACY_TOKEN);
    // Clear tenant context so the next user who logs in doesn't inherit a stale
    // X-Tenant-ID from the previous session (which would cause 403 on all
    // endpoints if the new user isn't a member of the old tenant).
    localStorage.removeItem('tenantId');
    localStorage.removeItem('tenantName');
    localStorage.removeItem('tenantSlug');
    // Also clear the legacy 'currentTenantId' key so no stale UUID survives.
    localStorage.removeItem('currentTenantId');
    sessionStorage.removeItem('currentTenantId');

    // Preserve the current path so the user returns here after login.
    const currentPath = `${window.location.pathname}${window.location.search}`;
    if (!currentPath.startsWith('/login')) {
      try {
        localStorage.setItem('redirectAfterLogin', currentPath);
      } catch {
        // Ignore write failure — redirect still works without it.
      }
    }
  } catch {
    // Ignore localStorage errors — we'll still try to navigate.
  }

  try {
    window.location.assign('/login');
  } catch {
    // Navigation blocked (test environment / CSP). No-op.
  }
}

/**
 * Evict the stored tenant context from localStorage/sessionStorage, UNLESS
 * it is a well-formed UUID — in which case it is preserved so an explicit
 * tenant-selector switch (superuser/global-admin) survives the reload the
 * selector itself triggers.
 *
 * Called at every healthy page load so that malformed/non-UUID tenant
 * values (e.g. the literal "undefined"/"null", or garbage left over from a
 * previous environment's database) cannot be used as X-Tenant-ID. A stale
 * but well-formed UUID from a different environment is not a security risk:
 * the view-level resolver (`resolve_tenant_for_view`) re-validates the
 * header against the authenticated user's access on every request and
 * falls back to the domain tenant / membership when it doesn't resolve.
 *
 * AuthContext.initializeAuth() still repairs the tenant context via
 * /tenants/current/ when no valid tenantId is stored at all.
 */
function clearStaleTenantContext(): void {
  try {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId || !isUuid(storedTenantId)) {
      localStorage.removeItem('tenantId');
      localStorage.removeItem('tenantName');
      localStorage.removeItem('tenantSlug');
    }
    // 'currentTenantId' is a legacy key no current code writes — always safe to evict.
    localStorage.removeItem('currentTenantId');
    sessionStorage.removeItem('currentTenantId');
  } catch {
    // Ignore — localStorage may be unavailable in private-browsing.
  }
}

/**
 * Run the session guard.
 *
 * Call this once at bootstrap, **before** rendering the React app.
 * Returns `true` if the session is healthy and the app should mount.
 * Returns `false` if a redirect has been initiated and rendering should
 * be skipped.
 */
export function runSessionGuard(): boolean {
  if (isSessionHealthy()) {
    // Evict any stale tenant context so that hard refreshes never carry a
    // stale X-Tenant-ID into the first batch of API calls.  The tenant is
    // re-established by AuthContext.initializeAuth() after auth is confirmed.
    clearStaleTenantContext();
    return true;
  }

  clearBrokenSession();
  return false;
}
